import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PhotoIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  FolderIcon,
  ArchiveBoxIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  EyeIcon,
  TrashIcon,
  ShareIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon as ArrowDownTrayIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const DataExport = () => {
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [exportHistory, setExportHistory] = useState([]);

  useEffect(() => {
    // Simulate fetching export data and history
    const fetchData = () => {
      const mockData = {
        // Personal Data
        personal: {
          profile: {
            name: 'Personal Profile',
            description: 'Your profile information, settings, and preferences',
            size: '2.4 MB',
            items: 1,
            lastUpdated: '2024-07-23T14:30:00Z',
            format: 'JSON',
            includes: ['Basic info', 'Contact details', 'Profile photo', 'Bio', 'Preferences']
          },
          settings: {
            name: 'Account Settings',
            description: 'All your account configuration and preferences',
            size: '156 KB',
            items: 1,
            lastUpdated: '2024-07-23T12:15:00Z',
            format: 'JSON',
            includes: ['Privacy settings', 'Notifications', 'Theme', 'Security settings']
          },
          authentication: {
            name: 'Authentication Data',
            description: 'Login history and security information',
            size: '890 KB',
            items: 1,
            lastUpdated: '2024-07-23T10:45:00Z',
            format: 'JSON',
            includes: ['Login history', 'Device list', '2FA settings', 'Password changes']
          }
        },

        // Work Data
        work: {
          documents: {
            name: 'Documents',
            description: 'All documents you have created or have access to',
            size: '245 MB',
            items: 387,
            lastUpdated: '2024-07-23T16:20:00Z',
            format: 'ZIP',
            includes: ['Created docs', 'Shared docs', 'Comments', 'Version history']
          },
          tasks: {
            name: 'Tasks & Projects',
            description: 'Your tasks, assignments, and project participation',
            size: '12.8 MB',
            items: 156,
            lastUpdated: '2024-07-23T15:30:00Z',
            format: 'JSON/CSV',
            includes: ['Assigned tasks', 'Created tasks', 'Project history', 'Time tracking']
          },
          calendar: {
            name: 'Calendar Data',
            description: 'Your appointments, meetings, and calendar events',
            size: '8.9 MB',
            items: 234,
            lastUpdated: '2024-07-23T14:00:00Z',
            format: 'ICS/JSON',
            includes: ['Appointments', 'Meetings', 'Reminders', 'Recurring events']
          },
          communications: {
            name: 'Messages & Communications',
            description: 'Your messages, chats, and communication history',
            size: '78.5 MB',
            items: 2847,
            lastUpdated: '2024-07-23T17:45:00Z',
            format: 'JSON',
            includes: ['Direct messages', 'Group chats', 'Notifications', 'Call history']
          }
        },

        // Medical Data
        medical: {
          patientRecords: {
            name: 'Patient Records',
            description: 'Patient information you have access to (anonymized)',
            size: '156 MB',
            items: 428,
            lastUpdated: '2024-07-23T13:20:00Z',
            format: 'JSON',
            includes: ['Case notes', 'Treatment history', 'Appointments', 'Lab results'],
            restricted: true
          },
          procedures: {
            name: 'Procedures & Treatments',
            description: 'Medical procedures and treatments you have performed',
            size: '34.7 MB',
            items: 89,
            lastUpdated: '2024-07-23T11:30:00Z',
            format: 'JSON',
            includes: ['Procedure logs', 'Treatment notes', 'Outcomes', 'Follow-ups'],
            restricted: true
          },
          training: {
            name: 'Training & Certifications',
            description: 'Your medical training records and certifications',
            size: '15.2 MB',
            items: 34,
            lastUpdated: '2024-07-20T09:15:00Z',
            format: 'PDF/JSON',
            includes: ['Certificates', 'Training logs', 'CPE credits', 'Assessments']
          }
        },

        // Analytics Data
        analytics: {
          activity: {
            name: 'Activity Analytics',
            description: 'Your usage patterns and activity statistics',
            size: '4.2 MB',
            items: 1,
            lastUpdated: '2024-07-23T18:00:00Z',
            format: 'JSON/CSV',
            includes: ['Login patterns', 'Feature usage', 'Time spent', 'Performance metrics']
          },
          performance: {
            name: 'Performance Data',
            description: 'Your performance metrics and evaluations',
            size: '2.8 MB',
            items: 12,
            lastUpdated: '2024-07-15T14:30:00Z',
            format: 'JSON/PDF',
            includes: ['Reviews', 'Feedback', 'Goals', 'Achievements']
          }
        }
      };

      const mockHistory = [
        {
          id: 1,
          name: 'Complete Profile Export',
          categories: ['personal', 'work'],
          date: '2024-07-15T10:30:00Z',
          size: '89.4 MB',
          format: 'ZIP',
          status: 'completed',
          downloadUrl: '#'
        },
        {
          id: 2,
          name: 'Document Backup',
          categories: ['work.documents'],
          date: '2024-07-01T16:20:00Z',
          size: '234 MB',
          format: 'ZIP',
          status: 'completed',
          downloadUrl: '#'
        },
        {
          id: 3,
          name: 'Personal Data Export',
          categories: ['personal'],
          date: '2024-06-15T09:45:00Z',
          size: '3.2 MB',
          format: 'JSON',
          status: 'completed',
          downloadUrl: '#'
        },
        {
          id: 4,
          name: 'Monthly Backup',
          categories: ['work', 'analytics'],
          date: '2024-06-01T00:00:00Z',
          size: '156 MB',
          format: 'ZIP',
          status: 'expired',
          downloadUrl: null
        }
      ];

      setExportData(mockData);
      setExportHistory(mockHistory);
      setLoading(false);
    };

    setTimeout(fetchData, 500);
  }, []);

  const handleCategoryToggle = (category) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  const handleExport = async (format = 'json') => {
    if (selectedCategories.size === 0) {
      alert('Please select at least one category to export.');
      return;
    }

    setExporting(true);
    
    // Simulate export process
    setTimeout(() => {
      const newExport = {
        id: Date.now(),
        name: `Data Export ${new Date().toLocaleDateString()}`,
        categories: Array.from(selectedCategories),
        date: new Date().toISOString(),
        size: '45.7 MB',
        format: format.toUpperCase(),
        status: 'completed',
        downloadUrl: '#'
      };
      
      setExportHistory(prev => [newExport, ...prev]);
      setSelectedCategories(new Set());
      setExporting(false);
      
      // Simulate file download
      alert(`Export completed! Your ${format.toUpperCase()} file is ready for download.`);
    }, 3000);
  };

  const calculateTotalSize = () => {
    let totalSize = 0;
    selectedCategories.forEach(category => {
      const [section, subsection] = category.split('.');
      if (subsection) {
        const item = exportData[section]?.[subsection];
        if (item) {
          totalSize += parseFloat(item.size) || 0;
        }
      } else {
        Object.values(exportData[section] || {}).forEach(item => {
          totalSize += parseFloat(item.size) || 0;
        });
      }
    });
    return totalSize.toFixed(1);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'expired':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCategoryIcon = (category) => {
    if (category.includes('documents')) return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
    if (category.includes('photos')) return <PhotoIcon className="h-5 w-5 text-green-500" />;
    if (category.includes('messages') || category.includes('communications')) return <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-500" />;
    if (category.includes('calendar')) return <CalendarIcon className="h-5 w-5 text-orange-500" />;
    if (category.includes('profile') || category.includes('personal')) return <UserCircleIcon className="h-5 w-5 text-indigo-500" />;
    if (category.includes('medical') || category.includes('patient')) return <ShieldCheckIcon className="h-5 w-5 text-red-500" />;
    return <FolderIcon className="h-5 w-5 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'personal', name: 'Personal Data', icon: UserCircleIcon },
    { id: 'work', name: 'Work Data', icon: DocumentTextIcon },
    { id: 'medical', name: 'Medical Data', icon: ShieldCheckIcon },
    { id: 'analytics', name: 'Analytics', icon: ArchiveBoxIcon }
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      {/* Custom Header for Data Export */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <ArrowDownTrayIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 sm:mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Data Export</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Download and backup your personal data
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
                <Link
                  to="/profile"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UserCircleIcon className="h-4 w-4 mr-2" />
                  Profile
                </Link>
                <Link
                  to="/profile/settings"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Important Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Data Export Information</h3>
              <div className="mt-2 text-sm text-blue-700 space-y-1">
                <p>• Your data export will include all information you have access to based on your permissions</p>
                <p>• Medical records are anonymized and encrypted according to HIPAA regulations</p>
                <p>• Export files are available for download for 30 days after generation</p>
                <p>• Large exports may take several minutes to complete</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Export */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Quick Export</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Export common data combinations with one click
                </p>
              </div>
              <div className="px-4 sm:px-6 py-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <button
                    onClick={() => {
                      setSelectedCategories(new Set(['personal.profile', 'personal.settings']));
                      handleExport('json');
                    }}
                    disabled={exporting}
                    className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <UserCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 mx-auto mb-2" />
                    <h4 className="text-sm font-semibold text-gray-900">Personal Data</h4>
                    <p className="text-xs text-gray-500 mt-1">Profile & Settings</p>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedCategories(new Set(['work.documents', 'work.tasks', 'work.calendar']));
                      handleExport('zip');
                    }}
                    disabled={exporting}
                    className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <DocumentTextIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <h4 className="text-sm font-semibold text-gray-900">Work Data</h4>
                    <p className="text-xs text-gray-500 mt-1">Documents, Tasks & Calendar</p>
                  </button>

                  <button
                    onClick={() => {
                      const allCategories = new Set();
                      Object.keys(exportData).forEach(section => {
                        Object.keys(exportData[section]).forEach(subsection => {
                          allCategories.add(`${section}.${subsection}`);
                        });
                      });
                      setSelectedCategories(allCategories);
                      handleExport('zip');
                    }}
                    disabled={exporting}
                    className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    <ArchiveBoxIcon className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                    <h4 className="text-sm font-semibold text-gray-900">Complete Export</h4>
                    <p className="text-xs text-gray-500 mt-1">All Available Data</p>
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <tab.icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Data Categories */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {tabs.find(t => t.id === activeTab)?.name} Categories
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Select the data categories you want to include in your export
                </p>
              </div>
              <div className="px-6 py-6">
                <div className="space-y-4">
                  {Object.entries(exportData[activeTab] || {}).map(([key, item]) => {
                    const categoryKey = `${activeTab}.${key}`;
                    const isSelected = selectedCategories.has(categoryKey);
                    
                    return (
                      <div
                        key={key}
                        className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        } ${item.restricted ? 'opacity-75' : ''}`}
                        onClick={() => !item.restricted && handleCategoryToggle(categoryKey)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mr-4">
                              {getCategoryIcon(key)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                                {item.restricted && (
                                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Restricted
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                              <div className="flex items-center mt-2 text-xs text-gray-500 space-x-4">
                                <span>Size: {item.size}</span>
                                <span>Items: {item.items.toLocaleString()}</span>
                                <span>Format: {item.format}</span>
                                <span>Updated: {new Date(item.lastUpdated).toLocaleDateString()}</span>
                              </div>
                              {item.includes && (
                                <div className="mt-2">
                                  <p className="text-xs text-gray-500">Includes:</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {item.includes.map((include, index) => (
                                      <span
                                        key={index}
                                        className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                                      >
                                        {include}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            {!item.restricted && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCategoryToggle(categoryKey)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Export History */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Export History</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Previous data exports and their download status
                </p>
              </div>
              <div className="px-6 py-6">
                <div className="space-y-4">
                  {exportHistory.map((export_item) => (
                    <div key={export_item.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-4">
                          {getStatusIcon(export_item.status)}
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{export_item.name}</h4>
                          <p className="text-sm text-gray-600">
                            {export_item.categories.join(', ')} • {export_item.size} • {export_item.format}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(export_item.date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {export_item.status === 'completed' && export_item.downloadUrl && (
                          <button
                            onClick={() => window.open(export_item.downloadUrl, '_blank')}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <ArrowDownTrayIcon className="h-3 w-3 mr-1" />
                            Download
                          </button>
                        )}
                        <button
                          onClick={() => alert('Export details functionality will be implemented')}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <EyeIcon className="h-3 w-3 mr-1" />
                          Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Export Summary */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Export Summary</h3>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Selected Categories</span>
                    <span className="font-medium">{selectedCategories.size}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Estimated Size</span>
                    <span className="font-medium">{calculateTotalSize()} MB</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-3">
                    <span className="text-gray-600">Export Status</span>
                    <span className={`font-medium ${exporting ? 'text-yellow-600' : 'text-green-600'}`}>
                      {exporting ? 'Processing...' : 'Ready'}
                    </span>
                  </div>
                </div>

                {selectedCategories.size > 0 && (
                  <div className="mt-6 space-y-2">
                    <button
                      onClick={() => handleExport('json')}
                      disabled={exporting}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {exporting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                      )}
                      {exporting ? 'Exporting...' : 'Export as JSON'}
                    </button>
                    <button
                      onClick={() => handleExport('zip')}
                      disabled={exporting}
                      className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      {exporting ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      ) : (
                        <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                      )}
                      {exporting ? 'Exporting...' : 'Export as ZIP'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              </div>
              <div className="px-6 py-4 space-y-3">
                <button
                  onClick={() => setSelectedCategories(new Set())}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Clear Selection
                </button>
                <Link
                  to="/privacy-policy"
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ShieldCheckIcon className="h-4 w-4 mr-2" />
                  Privacy Policy
                </Link>
                <Link
                  to="/data-usage"
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ShareIcon className="h-4 w-4 mr-2" />
                  Data Usage
                </Link>
              </div>
            </div>

            {/* Export Tips */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">Export Tips</h4>
                  <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                    <li>• Large exports may take several minutes</li>
                    <li>• JSON format is best for data analysis</li>
                    <li>• ZIP format preserves file structure</li>
                    <li>• Downloads expire after 30 days</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DataExport;
